import { useState } from 'react';
import { Card, CardContent, Stack, TextField, Button, Snackbar, Alert } from '@mui/material';
import { psiTokens, lightButtonSx } from '../theme/psiTheme';
import { sendContactMessage } from '../services/contactService';

const EMPTY_FORM = { fullName: '', company: '', email: '', service: '', description: '', website: '' };

export default function ContactForm() {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.fullName.trim() || !formData.email.trim() || !formData.description.trim()) {
      setStatus('error');
      setErrorMessage('Please fill in your name, email, and a description before sending.');
      return;
    }

    setStatus('sending');
    try {
      await sendContactMessage(formData);
      setStatus('sent');
      setFormData(EMPTY_FORM);
    } catch {
      setStatus('error');
      setErrorMessage("Something went wrong sending your message. Please try again, or email us directly.");
    }
  };

  return (
    <Card
      sx={{
        backgroundColor: psiTokens.background.primary,
        border: `1px solid ${psiTokens.border}`,
        borderRadius: '16px',
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Stack spacing={2.5} component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            name="fullName"
            placeholder="Full name"
            value={formData.fullName}
            onChange={handleChange}
            variant="outlined"
            size="small"
            required
            sx={{ '& .MuiOutlinedInput-root': { backgroundColor: psiTokens.background.secondary, borderRadius: '8px' } }}
          />
          <TextField
            fullWidth
            name="company"
            placeholder="Company / Organization"
            value={formData.company}
            onChange={handleChange}
            variant="outlined"
            size="small"
            sx={{ '& .MuiOutlinedInput-root': { backgroundColor: psiTokens.background.secondary, borderRadius: '8px' } }}
          />
          <TextField
            fullWidth
            name="email"
            type="email"
            placeholder="Email address"
            value={formData.email}
            onChange={handleChange}
            variant="outlined"
            size="small"
            required
            sx={{ '& .MuiOutlinedInput-root': { backgroundColor: psiTokens.background.secondary, borderRadius: '8px' } }}
          />
          <TextField
            fullWidth
            name="service"
            placeholder="Service of interest"
            value={formData.service}
            onChange={handleChange}
            variant="outlined"
            size="small"
            sx={{ '& .MuiOutlinedInput-root': { backgroundColor: psiTokens.background.secondary, borderRadius: '8px' } }}
          />
          <TextField
            fullWidth
            name="description"
            placeholder="Description of needs"
            value={formData.description}
            onChange={handleChange}
            variant="outlined"
            size="small"
            multiline
            rows={4}
            required
            sx={{ '& .MuiOutlinedInput-root': { backgroundColor: psiTokens.background.secondary, borderRadius: '8px' } }}
          />

          {/* Honeypot: real users never see or fill this in (visually
              hidden, not `display:none`, so most bots that skip
              display:none fields still find and fill it). Any non-empty
              value here tells contact.php to silently drop the submission. */}
          <TextField
            name="website"
            value={formData.website}
            onChange={handleChange}
            tabIndex={-1}
            autoComplete="off"
            sx={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
            aria-hidden="true"
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={status === 'sending'}
            sx={{
              ...lightButtonSx,
              fontSize: '13px',
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              padding: '12px',
              borderRadius: '10px',
            }}
          >
            {status === 'sending' ? 'Sending…' : 'Send Message'}
          </Button>
        </Stack>
      </CardContent>

      <Snackbar open={status === 'sent'} autoHideDuration={5000} onClose={() => setStatus('idle')}>
        <Alert severity="success" onClose={() => setStatus('idle')}>
          Message sent. We'll be in touch soon.
        </Alert>
      </Snackbar>

      <Snackbar open={status === 'error'} autoHideDuration={6000} onClose={() => setStatus('idle')}>
        <Alert severity="error" onClose={() => setStatus('idle')}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Card>
  );
}
